import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    forwardRef,
    Input,
    OnDestroy,
    OnInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    ControlValueAccessor,
    FormControl,
    FormsModule,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule
} from '@angular/forms';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacyOptionModule as MatOptionModule } from '@angular/material/legacy-core';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { TablerIconsModule } from 'angular-tabler-icons';
import {
    BehaviorSubject,
    combineLatest,
    Observable,
    ObservedValueOf,
    of,
    Subject
} from 'rxjs';
import { combineQueries, filterNilValue } from '@datorama/akita';
import {
    catchError,
    distinctUntilChanged,
    filter,
    map,
    shareReplay,
    startWith,
    switchMap,
    takeUntil,
    tap
} from 'rxjs/operators';
import { TestSelectorDirective } from '../../../directives/test-selector.directive';
import { AccountTypeEnum, Acct } from '../../../state/accts/acct.model';
import { AcctsQuery } from '../../../state/accts/accts.query';
import { InferFilter } from '../../../state/query-filter.functions';
import { MatLegacyCheckboxModule as MatCheckboxModule } from '@angular/material/legacy-checkbox';
import { AcctsService } from 'src/app/shared/state/accts/accts.service';
import { TagsQuery } from '../../../../features/new-tags/state/tags.query';
import capitalize from 'lodash/capitalize';

/** @deprecated - this component was built to simply sorting of nested account structures based on portfolios, legal entities and issuers.
 * It's possible to do that in other ways for most components now, so use this component sparingly, especially for new components. */
@Component({
    selector: 'sb-account-filter-select',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatOptionModule,
        MatSelectModule,
        TablerIconsModule,
        ReactiveFormsModule,
        TestSelectorDirective,
        MatCheckboxModule
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SbAccountFilterSelectComponent),
            multi: true
        }
    ],
    templateUrl: './sb-account-filter-select.component.html',
    styleUrls: ['./sb-account-filter-select.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SbAccountFilterSelectComponent
    implements OnInit, OnDestroy, ControlValueAccessor
{
    get includeBrokerAccounts(): boolean {
        return this._includeBrokerAccounts$.value;
    }

    get includeBrokerAccounts$(): Observable<boolean> {
        return this._includeBrokerAccounts$.asObservable();
    }

    /** includeBrokerAccounts
     * @param value - if true is passed, broker accounts will be included in options. <p>**Default:** `false`</p> */
    @Input() set includeBrokerAccounts(value: boolean) {
        this._includeBrokerAccounts$.next(value);
    }
    get valueKey(): keyof Acct {
        return this._valueKey;
    }

    @Input() set valueKey(value: keyof Acct) {
        this._valueKey = value;
    }
    private _valueKey: keyof Acct;
    get label(): string {
        return this._label;
    }

    @Input() set label(value: string) {
        this._label = value;
    }

    get sbTestSelector(): string {
        return this._sbTestSelector;
    }
    @Input() set sbTestSelector(value: string) {
        this._sbTestSelector = value;
    }

    constructor(
        private accountsQuery: AcctsQuery,
        private _accountsService: AcctsService,
        private _tagsQuery: TagsQuery,
        private _cdr: ChangeDetectorRef
    ) {
        if (this.accountsQuery.getAll().length === 0) {
            this._accountsService
                .get()
                .pipe(
                    catchError((err) => {
                        console.error(
                            'Account select control failed to fetch data:',
                            err
                        );
                        return of([]);
                    })
                )
                .subscribe();
        }
    }

    private _sbTestSelector: string = 'account-filter-select';
    private _includeBrokerAccounts$: BehaviorSubject<boolean> =
        new BehaviorSubject<boolean>(false);
    private _accountTypeFilter$ = this.includeBrokerAccounts$.pipe(
        map((include) => {
            return include
                ? {}
                : {
                      accountType: {
                          ne: AccountTypeEnum.BROKER
                      }
                  };
        }),
        shareReplay(1)
    );
    unsubscribe$ = new Subject();

    selectedAccounts: Acct[];

    accountSearchCtrl = new FormControl('', { nonNullable: true });
    optionSearchCtrl = new FormControl('', { nonNullable: true });
    tagSearchCtrl = new FormControl('', { nonNullable: true });
    compareAccountFn = (a: Acct, b: Acct) => {
        return a && b ? a.accountId === b.accountId : a === b;
    };

    private _label = 'Filter by';

    /** accountSelectorType
     * @description options to filter accounts by
     */
    private readonly _accountSelectorTypeOptions = [
        {
            label: 'Account',
            value: 'accountNumber',
            filterBy: 'accountNumber',
            tagBased: false
        },
        {
            label: 'Legal entity',
            value: 'owner',
            filterBy: 'ownerId',
            tagBased: false
        }
        /*{
            label: 'Portfolio',
            value: 'parentAccountClientAlias',
            filterBy: 'parentAccountClientAlias'
        }
        {
            label: 'Type of account',
            value: 'accountType',
            filterBy: 'accountType'
        }*/
    ];

    /** accountTagGroups$
     * @description observable for account tag groups, i.e. tag groups that are associated with accounts
     */
    private readonly _accountTagGroups$ = this._tagsQuery.hasLoaded$.pipe(
        startWith({}),
        switchMap((_) =>
            this._tagsQuery.asGroupMap$({
                itemType: {
                    eq: 'account'
                }
            })
        ),
        shareReplay(1)
    );

    /** accountSelectorTypeOptions$
     * @description observable for account selector type options, tag groups are added dynamically
     */
    accountSelectorTypeOptions$ = combineQueries([
        of(this._accountSelectorTypeOptions),
        this._accountTagGroups$
    ]).pipe(
        map(([options, tagGroups]) => {
            const tagOptions: Array<(typeof this._accountSelectorTypeOptions)[number]> =
                Object.keys(tagGroups).map((tagGroup) => {
                    return {
                        label: capitalize(tagGroup),
                        value: tagGroup,
                        filterBy: tagGroup,
                        tagBased: true
                    };
                });
            return [...options, ...tagOptions];
        })
    );

    /** accountSelectorTypeOptionsCtrl
     * @description form control for account selector type
     */
    accountSelectorTypeOptionsCtrl = new FormControl<
        (typeof this._accountSelectorTypeOptions)[number]
    >(this._accountSelectorTypeOptions[0]);

    /** accountSelectorType$
     * @description observable for accountSelectorType value
     * @returns {typeof this.accountSelectorTypeOptions[number]} accountSelectorType value as observable
     */
    accountSelectorType$ = this.accountSelectorTypeOptionsCtrl.valueChanges.pipe(
        startWith(this.accountSelectorTypeOptionsCtrl.value),
        shareReplay(1)
    );

    /** optionsCtrl
     * @description form control for options to choose from based on account selector type
     */
    optionsCtrl = new FormControl<Array<any>>(null);

    /** options$
     * @description observable for options to choose from based on account selector value
     * @returns {Array<any>} options to choose from based on account selector value
     */
    options$ = combineQueries([
        this.accountSelectorType$,
        this.optionSearchCtrl.valueChanges.pipe(startWith(this.optionSearchCtrl.value)),
        this._accountTypeFilter$
    ]).pipe(
        switchMap(([type, search, accountTypeFilter]) =>
            this.accountsQuery
                .selectAccountsByFilter$({
                    ...accountTypeFilter
                })
                .pipe(
                    map((accounts) => {
                        const options = {};
                        // loop through all accounts and get unique options
                        for (let i = 0; i < accounts.length; i++) {
                            const account = accounts[i];
                            const option = account[type.value];
                            // if option is not already in options, add it
                            if (!options[option]) {
                                options[option] = account;
                            }
                        }
                        return Object.values<string>(options).filter((option) => {
                            return search.length === 0
                                ? true
                                : option[type.value]
                                      ?.toLowerCase()
                                      .indexOf(search.toLowerCase()) > -1;
                        });
                    })
                )
        ),
        tap((options) => {
            // select all options when options change
            this.optionsCtrl.patchValue(options);
        }),
        shareReplay(1)
    );

    /** selectedOptions$
     * @description observable for selected options
     * @returns {Array<any>} selected options as observable
     */
    selectedOptions$ = this.optionsCtrl.valueChanges.pipe(
        startWith(this.optionsCtrl.value),
        shareReplay(1)
    );

    /** tagOptions$
     * @description observable for tag options to choose from, based on the selected tag group
     */
    tagOptions$ = combineQueries([
        this._accountTagGroups$,
        this.accountSelectorType$.pipe(
            filter((type) => type.tagBased),
            map((type) => type.filterBy),
            distinctUntilChanged()
        )
    ]).pipe(
        map(([tagGroups, group]) => {
            return Object.entries(tagGroups[group] || {}).map(([key, value]) => {
                return {
                    label: key,
                    description: value.description,
                    accountIds: Object.keys(value.account || {}).map(
                        (accountId) => +accountId as Acct['accountId']
                    )
                };
            });
        }),
        shareReplay(1)
    );

    /** filteredTagOptions$
     * @description observable for tag options to choose from, based on the selected tag group and search query
     */
    filteredTagOptions$ = combineQueries([
        this.tagOptions$,
        this.tagSearchCtrl.valueChanges.pipe(startWith(this.tagSearchCtrl.value || ''))
    ]).pipe(
        map(([options, search]) => {
            return options.filter((option) => {
                return search.length === 0
                    ? true
                    : option.label.toLowerCase().indexOf(search.toLowerCase()) > -1;
            });
        }),
        shareReplay(1)
    );

    tagCtrl = new FormControl<ObservedValueOf<typeof this.tagOptions$>>(null);
    selectedTags$ = this.tagCtrl.valueChanges.pipe(shareReplay(1));

    /** accountCtrl
     * @description form control for selected accounts
     */
    accountCtrl = new FormControl<Array<Acct | Acct['accountId']>>(null);

    /** filters$
     * @description observable for filters to apply to accounts query, based on account selector type and selected options for that type
     * @returns {QueryFilterNew<Acct>} filters to apply to accounts query as observable
     */
    filters$ = combineQueries([
        this.accountSelectorType$.pipe(filter((type) => !type.tagBased)),
        this.selectedOptions$,
        this._accountTypeFilter$
    ]).pipe(
        map(([type, selectedOptions, accountTypeFilter]) => {
            if (!selectedOptions?.length) return {};
            return {
                ...accountTypeFilter,
                [type.filterBy]: {
                    in: selectedOptions?.map((option) => option[type.filterBy])
                } as InferFilter<Acct>
            };
        }),
        shareReplay(1)
    );

    /** accounts$
     * @description observable for accounts returned by accounts query, based on filters
     * @returns {Acct[]} accounts returned by accounts query as observable
     */
    accounts$ = this.filters$.pipe(
        switchMap((filters) =>
            this.accountsQuery
                .selectAccountsByFilter$({
                    ...filters
                })
                .pipe(filter((accounts) => accounts && accounts.length > 0))
        ),
        tap((accounts) => {
            // value key is defined...
            if (this.valueKey) {
                // ...map accounts to value key
                accounts = accounts.map((account) => account[this.valueKey]);
            }
            // when parent selectors are changed we want all child accounts to be selected unless some accounts were initially passed to custom from control
            // if custom form has been touched or if no accounts were initially selected and if group is not tag based...
            if (
                (this.touched || !this.selectedAccounts) &&
                !this.accountSelectorTypeOptionsCtrl.value.tagBased
            ) {
                // ...select all accounts when accounts returned by query changes
                this.accountCtrl.patchValue(accounts);
            }
        }),
        shareReplay(1)
    );

    /** filteredAccounts$
     * @description observable for accounts returned by accounts query, based on filters and search query
     * @returns {Acct[]} accounts returned by accounts query as observable
     */
    filteredAccounts$ = combineLatest([
        this.accountSearchCtrl.valueChanges.pipe(startWith('')),
        this.filters$,
        this.accounts$
    ]).pipe(
        switchMap(([qs, filter, _]) =>
            this.accountsQuery
                .selectAccountsByFilter$({
                    accountClientAlias: {
                        qs
                    },
                    ...filter
                })
                .pipe(filterNilValue())
        ),
        shareReplay(1)
    );

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    touched = false;
    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    markAsTouched() {
        if (!this.touched) {
            this.onTouched();
            this.touched = true;
        }
    }
    writeValue(accounts): void {
        this.accountCtrl.setValue(accounts);
        this.selectedAccounts = accounts;
    }

    onChange = (accounts) => {};

    onTouched = () => {};

    @Input() control: FormControl;

    toggleOptions(checked: boolean, options: Array<any>) {
        if (checked) {
            this.optionsCtrl.patchValue(options);
        } else {
            this.optionsCtrl.patchValue([]);
        }
    }
    toggleAccounts(checked: boolean, accounts: Array<Acct>) {
        if (checked) {
            if (this.valueKey) {
                // ...map accounts to value key
                accounts = accounts.map((account) => account[this.valueKey]);
            }
            this.accountCtrl.patchValue(accounts);
        } else {
            this.accountCtrl.patchValue([]);
        }
    }

    toggleTags(checked: boolean, tags: Array<any>) {
        if (checked) {
            this.tagCtrl.patchValue(tags);
        } else {
            this.tagCtrl.patchValue([]);
        }
    }

    ngOnInit(): void {
        this.accountCtrl.valueChanges
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((accounts) => {
                this.onChange(accounts);
                if (this.control && !this.touched) {
                    this.control.markAsPristine();
                }
            });

        // when account selector type changes...
        this.accountSelectorType$
            .pipe(
                // ...filter selector and only emit when selector is tag based
                filter((type) => type.tagBased),
                // ...switch to tag options observable
                switchMap((type) => {
                    return this.tagOptions$;
                }),
                // ...patch tag control with tag options, i.e. select all available tags by default
                tap((options) => {
                    this.tagCtrl.patchValue(options);
                }),
                takeUntil(this.unsubscribe$)
            )
            .subscribe();

        // when selected tags change...
        this.selectedTags$
            .pipe(
                tap((selectedTags) => {
                    // ...get unique account ids from selected tags...
                    const uniqueAccountIds: Array<Acct['accountId']> = [
                        ...new Set(
                            selectedTags?.reduce((acc, tag) => {
                                return [...acc, ...tag.accountIds];
                            }, []) || []
                        )
                    ];
                    // ...patch account control with unique account ids
                    this.accountCtrl.patchValue(uniqueAccountIds);
                }),
                takeUntil(this.unsubscribe$)
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next(true);
        this.unsubscribe$.complete();
    }
}
